import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  FormControlLabel,
  Radio,
  Box,
  Chip,
  Typography,
  FormGroup,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import {
  getCropTypeKeys,
  ICropType,
  normalizeCropTypeKey,
} from "../../../types/cropType";
import { ICompany } from "../../../types/company";
import cropTypeService from "../../../service/cropTypeService";
import { useAppContext } from "../../../contexts/useStorageContext";
import { loadCropTypes } from "../../../helpers/loadCropTypes";
import CompanyAutoComplete from "../Common/CompanyAutoComplete";
import { sortBy } from "lodash";
import GeneralPopoverWrapper from "../Common/GeneralPopoverWrapper";
import CropTypeChemicalFieldsPopoverContent from "./CropTypeChemicalFieldsPopoverContent";
import { ChemicalFieldGroup } from "../../../types/chemicals";

interface CropTypeFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (cropType: ICropType) => void;
  initialCropType?: ICropType;
}

const CropTypeFormDialog: React.FC<CropTypeFormDialogProps> = ({
  open,
  onClose,
  onSave,
  initialCropType,
}) => {
  const { t } = useTranslation();
  const [cropType, setCropType] = useState<Partial<ICropType>>(
    initialCropType || {
      name: "",
      description: "",
      isPublic: true,
      availablePredictionTargets: [],
      aliases: [],
      chemicalFields: [],
    }
  );
  const [selectedCompanies, setSelectedCompanies] = useState<ICompany[]>([]);
  const [newCompanyPredictionTargets, setNewCompanyPredictionTargets] =
    useState<Record<string, string[]>>({});
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const { state, dispatch } = useAppContext();
  const existingCropTypes = state.cropTypes || [];
  const [aliasInput, setAliasInput] = useState("");

  useEffect(() => {
    if (open) {
      setCropType(
        initialCropType || {
          name: "",
          description: "",
          isPublic: true,
          availablePredictionTargets: [],
          aliases: [],
          chemicalFields: [],
        }
      );
      setSelectedCompanies(initialCropType?.companies || []);
      setNewCompanyPredictionTargets({});
      setAutocompleteKey(0);
      setAliasInput("");
    }
  }, [open, initialCropType]);

  const addAlias = (rawValue?: string) => {
    const nextAlias = (rawValue ?? aliasInput).trim();
    if (!nextAlias) return;
    const existingAliases = cropType.aliases || [];
    const alreadyAdded = existingAliases.some(
      (alias) => normalizeCropTypeKey(alias) === normalizeCropTypeKey(nextAlias)
    );
    if (!alreadyAdded) {
      setCropType({
        ...cropType,
        aliases: [...existingAliases, nextAlias],
      });
    }
    setAliasInput("");
  };

  const removeAlias = (aliasToRemove: string) => {
    setCropType({
      ...cropType,
      aliases: (cropType.aliases || []).filter(
        (alias) => alias !== aliasToRemove
      ),
    });
  };

  const togglePredictionTarget = (target: string, checked: boolean) => {
    const current = cropType.availablePredictionTargets || [];
    setCropType({
      ...cropType,
      availablePredictionTargets: checked
        ? [...current, target]
        : current.filter((t) => t !== target),
    });
  };

  const getCompanyPredictionTargets = (company: ICompany, cropTypeId?: string) => {
    if (cropTypeId) {
      return (
        company.allowedPredictionTargetsForCropTypes?.find(
          (target) => target.cropTypeId === cropTypeId
        )?.predictionTargets || []
      );
    }
    return cropType.availablePredictionTargets || [];
  };

  const updateCompanyPredictionTarget = (
    companyId: string,
    target: string,
    checked: boolean
  ) => {
    const cropTypeId = initialCropType?._id;
    if (!cropTypeId) {
      const existingTargets = newCompanyPredictionTargets[companyId] || [];
      setNewCompanyPredictionTargets({
        ...newCompanyPredictionTargets,
        [companyId]: checked
          ? [...existingTargets, target]
          : existingTargets.filter((t) => t !== target),
      });
      return;
    }

    setSelectedCompanies(
      selectedCompanies.map((company) => {
        if (company._id !== companyId) return company;

        const existingTargets = getCompanyPredictionTargets(company, cropTypeId);
        const predictionTargets = checked
          ? [...existingTargets, target]
          : existingTargets.filter((t) => t !== target);

        const otherTargets =
          company.allowedPredictionTargetsForCropTypes?.filter(
            (entry) => entry.cropTypeId !== cropTypeId
          ) || [];

        return {
          ...company,
          allowedPredictionTargetsForCropTypes: [
            ...otherTargets,
            { cropTypeId, predictionTargets },
          ],
        };
      })
    );
  };

  const syncCompanies = async (
    cropTypeId: string,
    originalCompanies: ICompany[]
  ) => {
    const newlyAddedCompanies = selectedCompanies.filter(
      (company) =>
        !originalCompanies?.map((c) => c._id).includes(company._id)
    );
    const newlyRemovedCompanies = originalCompanies.filter(
      (company) => !selectedCompanies?.map((c) => c._id).includes(company._id)
    );

    if (newlyAddedCompanies.length > 0) {
      const companyIdToPredictionTargets = newlyAddedCompanies.reduce(
        (acc, company) => {
          acc[company._id as string] =
            company.allowedPredictionTargetsForCropTypes?.find(
              (target) => target.cropTypeId === cropTypeId
            )?.predictionTargets ||
            cropType.availablePredictionTargets ||
            [];
          return acc;
        },
        {} as Record<string, string[]>
      );
      await cropTypeService.addAssignedCompanies({
        id: cropTypeId,
        companyIds: newlyAddedCompanies.map((company) => company._id as string),
        companyIdToPredictionTargets,
      });
    }

    if (newlyRemovedCompanies.length > 0) {
      await cropTypeService.removeAssignedCompanies({
        id: cropTypeId,
        companyIds: newlyRemovedCompanies.map((company) => company._id as string),
      });
    }

    const predictionTargetUpdatedCompanies = selectedCompanies.filter(
      (company) => {
        const originalCompany = originalCompanies?.find(
          (c) => c._id == company._id
        );
        if (!originalCompany) return false;
        const originalCompanyTargets = sortBy(
          originalCompany.allowedPredictionTargetsForCropTypes || [],
          (v) => v.cropTypeId
        )
          .map((v) => v.predictionTargets?.sort()?.join(",") || "")
          .join(",");
        const updatedCompanyTargets = sortBy(
          company.allowedPredictionTargetsForCropTypes || [],
          (v) => v.cropTypeId
        )
          .map((v) => v.predictionTargets?.sort()?.join(",") || "")
          .join(",");
        return originalCompanyTargets !== updatedCompanyTargets;
      }
    );
    if (predictionTargetUpdatedCompanies.length) {
      await cropTypeService.updateCompanyPredictions({
        companies: predictionTargetUpdatedCompanies,
      });
    }
  };

  const handleSubmit = async () => {
    try {
      let result: ICropType | null = null;

      if (initialCropType?._id) {
        await cropTypeService.update({
          id: initialCropType._id,
          updateData: cropType as ICropType,
        });
        result = { ...initialCropType, ...cropType } as ICropType;

        if (cropType.isPublic) {
          const assignedCompanies = initialCropType.companies || [];
          if (assignedCompanies.length) {
            await cropTypeService.removeAssignedCompanies({
              id: initialCropType._id,
              companyIds: assignedCompanies.map((c) => c._id as string),
            });
          }
        } else {
          await syncCompanies(
            initialCropType._id,
            initialCropType.companies || []
          );
        }
      } else {
        result = await cropTypeService.addNewCropType(cropType as ICropType);
        if (result?._id && !cropType.isPublic && selectedCompanies.length) {
          await cropTypeService.addAssignedCompanies({
            id: result._id,
            companyIds: selectedCompanies.map((c) => c._id as string),
            companyIdToPredictionTargets: selectedCompanies.reduce(
              (acc, company) => {
                acc[company._id as string] =
                  newCompanyPredictionTargets[company._id as string] ||
                  cropType.availablePredictionTargets ||
                  [];
                return acc;
              },
              {} as Record<string, string[]>
            ),
          });
        }
      }

      await loadCropTypes(dispatch);
      onSave(result?._id ? result : (cropType as ICropType));
      onClose();
    } catch (error) {
      console.error("Error saving crop type:", error);
    }
  };

  const isSelective = !cropType.isPublic;
  const availableTargets = cropType.availablePredictionTargets || [];
  const chemicalFields = cropType.chemicalFields || [];
  const ftiFieldCount = chemicalFields.filter(
    (field) => field.group === ChemicalFieldGroup.FTI
  ).length;
  const nagFieldCount = chemicalFields.filter(
    (field) => field.group === ChemicalFieldGroup.NAG
  ).length;
  const normalizedName = normalizeCropTypeKey(cropType.name);
  const aliasKeys = (cropType.aliases || [])
    .map((a) => normalizeCropTypeKey(a))
    .filter(Boolean);
  const isDuplicateName =
    !!normalizedName &&
    existingCropTypes.some((existing) => {
      if (existing._id === initialCropType?._id) return false;
      return getCropTypeKeys(existing).includes(normalizedName);
    });
  const isDuplicateAlias =
    aliasKeys.length > 0 &&
    (aliasKeys.some((key) => key === normalizedName) ||
      existingCropTypes.some((existing) => {
        if (existing._id === initialCropType?._id) return false;
        const existingKeys = getCropTypeKeys(existing);
        return aliasKeys.some((key) => existingKeys.includes(key));
      }));
  const canSubmit =
    !!cropType.name &&
    !!cropType.description &&
    availableTargets.length > 0 &&
    (!isSelective || selectedCompanies.length > 0) &&
    !isDuplicateName &&
    !isDuplicateAlias;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        {initialCropType ? t("Edit Crop Type") : t("Add New Crop Type")}
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          margin="dense"
          name="name"
          label={t("Name")}
          fullWidth
          value={cropType.name}
          onChange={(event) =>
            setCropType({ ...cropType, name: event.target.value })
          }
          size="small"
          sx={{ mb: 2 }}
          error={isDuplicateName}
          helperText={
            isDuplicateName
              ? t("A crop type with this name already exists")
              : undefined
          }
        />
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              margin="dense"
              label={t("Aliases")}
              fullWidth
              value={aliasInput}
              onChange={(event) => setAliasInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addAlias();
                }
              }}
              size="small"
              error={isDuplicateAlias}
              helperText={
                isDuplicateAlias
                  ? t(
                      "An alias conflicts with an existing crop type name or alias"
                    )
                  : t("Type an alternate name, then tap Add")
              }
            />
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => addAlias()}
              disabled={!aliasInput.trim()}
              sx={{
                mt: "8px",
                minHeight: 40,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {t("Add")}
            </Button>
          </Box>
          {(cropType.aliases || []).length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
              {(cropType.aliases || []).map((alias) => (
                <Chip
                  key={alias}
                  variant="outlined"
                  label={alias}
                  size="small"
                  onDelete={() => removeAlias(alias)}
                />
              ))}
            </Box>
          ) : null}
        </Box>
        <TextField
          margin="dense"
          name="address"
          label={t("Description")}
          fullWidth
          value={cropType.description}
          onChange={(event) =>
            setCropType({ ...cropType, description: event.target.value })
          }
          size="small"
          sx={{ mb: 2 }}
        />
        <Box
          sx={{
            mb: 2,
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t("Available Prediction")}
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={availableTargets.includes("FTI")}
                    onChange={(e) =>
                      togglePredictionTarget("FTI", e.target.checked)
                    }
                  />
                }
                label="FTI"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={availableTargets.includes("NAG")}
                    onChange={(e) =>
                      togglePredictionTarget("NAG", e.target.checked)
                    }
                  />
                }
                label="NAG"
              />
            </FormGroup>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {t("Chemical Fields")}
            </Typography>
            <GeneralPopoverWrapper
              anchorHorizontal="right"
              transformHorizontal="left"
              anchorVertical="top"
              triggerElement={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    cursor: "pointer",
                  }}
                >
                  <Chip
                    color={ftiFieldCount > 0 ? "warning" : "default"}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        FTI
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {ftiFieldCount}
                        </Box>
                      </Box>
                    }
                  />
                  <Chip
                    color={nagFieldCount > 0 ? "warning" : "default"}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        NAG (Wet chem)
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {nagFieldCount}
                        </Box>
                      </Box>
                    }
                  />
                </Box>
              }
              popoverContent={
                <Box sx={{ minHeight: "500px"}}>
                <CropTypeChemicalFieldsPopoverContent
                  chemicalFields={chemicalFields}
                  onChange={(fields) =>
                    setCropType({ ...cropType, chemicalFields: fields })
                  }
                  cropTypes={existingCropTypes}
                  currentCropTypeId={cropType._id}
                />
                </Box>
              }
            />
          </Box>
        </Box>
        <FormControlLabel
          control={
            <Radio
              checked={!!cropType.isPublic}
              onChange={() => {
                setCropType({ ...cropType, isPublic: true });
                setSelectedCompanies([]);
              }}
            />
          }
          label={t("Public Crop Type (All companies can use this crop type)")}
        />
        <FormControlLabel
          control={
            <Radio
              checked={!cropType.isPublic}
              onChange={() => setCropType({ ...cropType, isPublic: false })}
            />
          }
          label={t(
            "Selective Crop Type (Only selected companies can use this crop type)"
          )}
        />
        {isSelective ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t("Assigned Companies")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", mb: 1 }}>
              {selectedCompanies.map((company) => (
                <Box key={company._id} sx={{ width: "100%", mb: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Chip
                      label={company.name}
                      onDelete={() => {
                        setSelectedCompanies(
                          selectedCompanies.filter((c) => c._id !== company._id)
                        );
                        if (!initialCropType?._id && company._id) {
                          const updatedTargets = { ...newCompanyPredictionTargets };
                          delete updatedTargets[company._id];
                          setNewCompanyPredictionTargets(updatedTargets);
                        }
                      }}
                    />
                    <FormGroup row sx={{ ml: 1 }}>
                      {availableTargets.includes("FTI") ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={
                                initialCropType?._id
                                  ? getCompanyPredictionTargets(
                                      company,
                                      initialCropType._id
                                    ).includes("FTI")
                                  : (
                                      newCompanyPredictionTargets[
                                        company._id as string
                                      ] || availableTargets
                                    ).includes("FTI")
                              }
                              onChange={(e) =>
                                updateCompanyPredictionTarget(
                                  company._id as string,
                                  "FTI",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="FTI"
                        />
                      ) : null}
                      {availableTargets.includes("NAG") ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={
                                initialCropType?._id
                                  ? getCompanyPredictionTargets(
                                      company,
                                      initialCropType._id
                                    ).includes("NAG")
                                  : (
                                      newCompanyPredictionTargets[
                                        company._id as string
                                      ] || availableTargets
                                    ).includes("NAG")
                              }
                              onChange={(e) =>
                                updateCompanyPredictionTarget(
                                  company._id as string,
                                  "NAG",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="NAG"
                        />
                      ) : null}
                    </FormGroup>
                  </Box>
                </Box>
              ))}
            </Box>
            <CompanyAutoComplete
              fullWidth
              key={`${autocompleteKey}-add-company`}
              label={t("Add Company")}
              value={undefined}
              onChange={(selectedCompany) => {
                if (
                  selectedCompany &&
                  !selectedCompanies.some((c) => c._id === selectedCompany._id)
                ) {
                  if (initialCropType?._id && selectedCompany._id) {
                    setSelectedCompanies([
                      ...selectedCompanies,
                      {
                        ...selectedCompany,
                        allowedPredictionTargetsForCropTypes: [
                          ...(
                            selectedCompany.allowedPredictionTargetsForCropTypes ||
                            []
                          ).filter(
                            (entry) =>
                              entry.cropTypeId !== initialCropType._id
                          ),
                          {
                            cropTypeId: initialCropType._id,
                            predictionTargets: availableTargets,
                          },
                        ],
                      },
                    ]);
                  } else {
                    setSelectedCompanies([
                      ...selectedCompanies,
                      selectedCompany,
                    ]);
                    if (selectedCompany._id) {
                      setNewCompanyPredictionTargets({
                        ...newCompanyPredictionTargets,
                        [selectedCompany._id]: availableTargets,
                      });
                    }
                  }
                }
                setAutocompleteKey((prev) => prev + 1);
              }}
            />
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button
          disabled={!canSubmit}
          onClick={handleSubmit}
          color="primary"
        >
          {initialCropType ? t("Update") : t("Create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CropTypeFormDialog;
